import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ROUTES } from "@/constants/routes";
import { pageViewStorage } from "@/lib/storage/pageView";
import {
  useGetRunQuery,
  useGetTaskTypesQuery,
  useRunTaskTypeMutation,
} from "@/services/api-hooks/content";
import { gtmService } from "@/services/gtm-service";
import { trackCreatePageView } from "@/services/gtm-service/create/trackCreatePageView";
import type { TaskType } from "@/types/content";
import { parseRunInputs } from "@/utils/run";

import type { FormValues, GenContentAnalytics } from "../types";
import {
  buildInputs,
  canSubmit,
  defaultValues,
  isPresent,
  isTrendTaskType,
} from "../utils";

const buildAnalytics = (taskType: TaskType, values: FormValues): GenContentAnalytics => ({
  use_case: taskType.id,
  archetype: String(values.appearance ?? "custom"),
  arch_prompt: (values.appearance_prompt as string) || undefined,
  background: String(values.background ?? ""),
  bg_prompt: (values.background_prompt as string) || undefined,
  style: String(values.style ?? values.carousel_style ?? ""),
  pacing: String(values.pacing ?? ""),
  subtitles: Boolean(values.subtitles),
  content_source: isPresent(values.tiktok_url) ? "tiktok" : "script",
  script_text: (values.script as string) || undefined,
  tiktok_url: (values.tiktok_url as string) || undefined,
});

export const useCreateForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromRunId = searchParams.get("from");

  const {
    data: taskTypesData,
    isLoading: isTaskTypesLoading,
    isError: isTaskTypesError,
    error: taskTypesError,
  } = useGetTaskTypesQuery();
  const {
    data: runData,
    isFetched: isRunFetched,
    isError: isRunError,
    error: runError,
  } = useGetRunQuery(fromRunId ?? undefined);
  const { mutate: runTaskTypeMutation, isPending: isRunTaskTypePending } =
    useRunTaskTypeMutation();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [values, setValues] = useState<FormValues>({});
  const [prefillApplied, setPrefillApplied] = useState(false);
  const isPrefilling = !!fromRunId && !prefillApplied;

  const initializedForRef = useRef<string | null>(null);

  const taskTypes = useMemo(() => {
    if (!taskTypesData) return [];

    return taskTypesData.filter((t) => !isTrendTaskType(t));
  }, [taskTypesData]);

  const selectedTaskType = useMemo(
    () => taskTypes.find((t) => t.id === selectedId) ?? null,
    [taskTypes, selectedId],
  );

  const canGenerate = canSubmit(selectedTaskType ?? undefined, values);

  const mergeValues = useCallback(
    (patch: FormValues) => setValues((prev) => ({ ...prev, ...patch })),
    [],
  );

  useEffect(() => {
    const view_count = pageViewStorage.incrementViewCount(ROUTES.CREATE);
    trackCreatePageView({ view_count });
  }, []);

  useEffect(() => {
    if (isTaskTypesError) toast.error("Failed to load content types. Please try again.");
  }, [isTaskTypesError]);

  useEffect(() => {
    if (isRunError) toast.error("Failed to load previous content settings.");
  }, [isRunError]);

  useEffect(() => {
    if (!selectedId && taskTypes.length) setSelectedId(taskTypes[0].id);
  }, [selectedId, taskTypes]);

  useEffect(() => {
    if (!selectedTaskType) return;
    if (fromRunId && !prefillApplied) return;
    if (initializedForRef.current === selectedTaskType.id) return;
    initializedForRef.current = selectedTaskType.id;
    setValues(defaultValues(selectedTaskType));
  }, [selectedTaskType, fromRunId, prefillApplied]);

  useEffect(() => {
    if (!fromRunId || prefillApplied) return;
    if (!taskTypes.length || !isRunFetched) return;

    if (runData) {
      const taskType = taskTypes.find((t) => t.id === runData.task_type_id);

      if (taskType) {
        setSelectedId(runData.task_type_id);
        setValues({
          ...(taskType ? defaultValues(taskType) : {}),
          ...(parseRunInputs(runData.inputs_json) as FormValues),
        });
        initializedForRef.current = runData.task_type_id;
        toast.success("Loaded settings from previous content", { icon: "📋" });
      }
    }
    setPrefillApplied(true);
  }, [fromRunId, prefillApplied, taskTypes, isRunFetched, runData]);

  const handleGenerate = () => {
    if (!selectedTaskType || !canGenerate || isRunTaskTypePending) return;

    gtmService.trackGenContentTry(buildAnalytics(selectedTaskType, values));

    runTaskTypeMutation(
      {
        taskTypeId: selectedTaskType.id,
        req: { inputs: buildInputs(selectedTaskType, values), mode: "async" },
      },
      {
        onSuccess: () => {
          toast.success("Content queued! Generation takes a few minutes.", {
            duration: 5000,
            icon: "🎬",
          });
          navigate(ROUTES.VIDEOS);
        },
        onError: (err) => {
          const errorMessage =
            err instanceof Error ? err.message : "Failed to start generation";
          gtmService.trackGenContentFail({ stage: "submission", error: errorMessage });
          toast.error(errorMessage);
        },
      },
    );
  };

  return {
    meta: {
      isLoading: isTaskTypesLoading || isPrefilling,
      fromRunId,
      isError: isTaskTypesError || isRunError,
      error: taskTypesError ?? runError ?? null,
    },
    taskTypes,
    selectedTaskType,
    selectTaskType: setSelectedId,
    values,
    setValues: mergeValues,
    submit: {
      canGenerate,
      isSubmitting: isRunTaskTypePending,
      handleGenerate,
      generateButtonLabel: fromRunId ? "Regenerate" : "Generate",
    },
  };
};

export type CreateForm = ReturnType<typeof useCreateForm>;
